import { useEffect, useLayoutEffect, useRef } from 'preact/compat';
import { Text } from 'preact-i18n';
import { useState } from 'preact/hooks';
import { camelizeKeys, decamelizeKeys } from 'humps';
import createUrl from '../../utils/createUrl';
import normalize from '../../utils/normalize';
import mixpanelTracker from '../../utils/mixpanelTracker';
import SelectContext from './context';
import { formatDisplayType, isCityAllTerminalsPlace } from './displayTypes';
import { fetchPlace } from '../../utils/fetchPlace';
import SearchInput from '../SearchInput';
import getProductType from '../../utils/getProductType';
import { PlacesProvider } from '../../context/Places';
import { filterByAllowedSlugs } from '../../utils/allowedSlugs';
import { filterByBasePlaceType, getActiveBasePlaceType } from '../../utils/basePlaceType';
import { shouldUseDestinationFirstPrefetchLookup } from '../../utils/destinationFirstPrefetch';
import {
  PLACES_GROUP_FALLBACK,
  PLACES_SORT,
  applyPlacesSortParam,
  sortPlaces,
} from '../../utils/placesSort';
import '../../styles/AutocompleteSelect.min.css';

/** The place field the dropdown groups by when `data-prop-group-places` is on. */
const GROUP_FIELD = 'state';

/** Shared so clearing an already empty list doesn't re-render. */
const NO_UNAVAILABLE_OPTIONS = [];

const AutocompleteSelect = ({
  field,
  error,
  hasArrow,
  placeholder,
  label,
  sourceUrl,
  displayType,
  isPackagesSearch,
  onChange,
  onInputChange,
  onNoSearchResults,
  place: placeProp,
  oppositePlace,
  from,
  to,
  initialPlaceSlug,
  orderDestinationHit,
  coordsToOrder,
  allowFetch,
  flatVariant,
  originHasInitialSlug,
  showNearestTerminal,
  nearestTerminal,
  onFocus,
  didInitialOriginChanged,
  groupPlaces,
  disabled = false,
  isDestinationFirstEnabled = false,
  destinationFirstPrefetch = false,
  allowedSlugs = null,
  hybridSearchEnabled = false,
  defaultPlaceType = null,
  placesSort = PLACES_SORT.POPULARITY,
  placesSearchSort = PLACES_SORT.POPULARITY,
}) => {
  const [input, setInput] = useState('');
  const [places, setPlaces] = useState([]);
  const [options, setOptions] = useState([]);
  // City all-terminals matches the one-side rule hides: shown as unavailable, never selectable.
  const [unavailableOptions, setUnavailableOptions] = useState(NO_UNAVAILABLE_OPTIONS);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [hasRequestError, setHasRequestError] = useState(false);
  const [disabledInput, setDisabledInput] = useState(disabled);
  const [showItems, setShowItems] = useState(false);
  const [originsLoaded, setOriginsLoaded] = useState(false);
  const orderByGeoFetched = useRef(false);
  const containerRef = useRef(null);
  const inputRef = useRef({});
  const optionsRef = useRef(null);
  const debounceRef = useRef(null);
  // The typed search keeps its own timer: the no-results effect clears `debounceRef` on every options change.
  const typedRequestTimeoutRef = useRef(null);
  const typedRequestIdRef = useRef(0);
  const typedRequestLoadingRef = useRef(false);
  const showingInitialSlug = useRef(false);
  // Bumped when the initial lookup starts or the user picks/types, so a late lookup can't overwrite their choice.
  const initialLookupIdRef = useRef(0);
  // Id of the latest initial lookup. Typing or picking bumps `initialLookupIdRef` past it.
  const initialLookupStartedIdRef = useRef(null);
  const settledInitialSlugRef = useRef(null);
  // `undefined` marks the first render; the active type itself can be `null`.
  const previousActiveBasePlaceTypeRef = useRef(undefined);
  // Bumped by every no-text list request, so a stale list response can't overwrite or clear a newer one.
  const listRequestIdRef = useRef(0);
  const isMouseOverOptionsRef = useRef(false);
  const selectedPlaceRef = useRef(null);
  const isEditingInputRef = useRef(false);

  // An initial route can resolve one field before the other; keep its pending field quiet.
  const canAutoOpenList =
    !isDestinationFirstEnabled || !initialPlaceSlug || settledInitialSlugRef.current === initialPlaceSlug;

  /**
   * It checks which input ref should be used based on the product type (web or web-mobile).
   * @returns String - The current input ref based on the product type
   */
  const currentInputRef = () => {
    const isMobile = getProductType() === 'web-mobile';
    return isMobile ? inputRef.current?.mobile : inputRef.current?.desktop;
  };

  const handleClickOutside = event => {
    // If it is a mobile screen the options are not hidden when the user clicks outside the container
    const isMobile = getProductType() === 'web-mobile';
    if (!isMobile && containerRef && !containerRef.current.contains(event.target)) {
      setShowItems(false);
    }
  };

  const handleDisableField = state => {
    if (disabled) {
      setDisabledInput(true);
      return;
    }

    setDisabledInput(state);
  };

  const filterCityAllTerminalsOptions = data => {
    const shouldHideCityAllTerminals =
      !hybridSearchEnabled || isCityAllTerminalsPlace(oppositePlace, hybridSearchEnabled);

    if (!shouldHideCityAllTerminals) return data;

    return data.filter(place => !isCityAllTerminalsPlace(place, hybridSearchEnabled));
  };

  const prioritizeCityAllTerminalsOptions = data => [
    ...data.filter(place => isCityAllTerminalsPlace(place, hybridSearchEnabled)),
    ...data.filter(place => !isCityAllTerminalsPlace(place, hybridSearchEnabled)),
  ];

  // Only a control-group (hybrid off) user gets the base type. Applied here, never to the
  // corpus kept by `setPlaces`, so a hybrid value that settles later can bring cities back.
  const activeBasePlaceType = getActiveBasePlaceType({
    basePlaceType: defaultPlaceType,
    hybridSearchEnabled,
  });
  // Read through a ref: a response that lands after the hybrid value settles must use the
  // current type, not the one captured when its request started.
  const activeBasePlaceTypeRef = useRef(activeBasePlaceType);
  activeBasePlaceTypeRef.current = activeBasePlaceType;

  const preparePlaces = data =>
    filterByBasePlaceType(
      filterCityAllTerminalsOptions(filterByAllowedSlugs(data || [], allowedSlugs)),
      activeBasePlaceTypeRef.current
    );

  // A frozen field cannot be changed, so it keeps its fixed initial place whatever its type.
  const prepareInitialPlaces = data =>
    disabled ? filterCityAllTerminalsOptions(filterByAllowedSlugs(data || [], allowedSlugs)) : preparePlaces(data);

  /**
   * Builds the places URL for the paths with no typed text. Every branch returns the url
   * object rather than its href, so `placesUrl` below owns the only conversion — and with
   * it the only place the ordering parameter has to be applied. A branch added later
   * inherits both instead of having to remember them.
   */
  const buildPlacesUrl = () => {
    const url = createUrl(sourceUrl);
    const { lat, long } = coordsToOrder || {};

    const useDestinationPrefetch = shouldUseDestinationFirstPrefetchLookup({
      field,
      destinationFirstPrefetch,
      from,
    });

    if (useDestinationPrefetch) {
      return url.setQueryParam('prefetch', 'true');
    }

    if (from) {
      url.setQueryParam('from', from);
      if (orderDestinationHit) url.setQueryParam('order_by', 'origin_hits');
    } else if (to) {
      url.setQueryParam('to', to);
      if (orderDestinationHit) url.setQueryParam('order_by', 'destination_hits');
    } else {
      url.setQueryParam('prefetch', 'true');
      if (orderDestinationHit) url.setQueryParam('order_by', 'origin_hits');
    }
    if (lat && long && coordsToOrder && field === 'origin')
      url.setQueryParam('lat', lat).setQueryParam('long', long);

    return url;
  };

  const placeLookupUrl = query => {
    const url = createUrl(sourceUrl);
    url.setQueryParam('q', query);

    return url.href;
  };

  const setSelectedPlaceInput = place => {
    isEditingInputRef.current = false;
    setInput(formatDisplayType(displayType, place, isPackagesSearch, undefined, hybridSearchEnabled));
  };

  const selectInitialPlace = place => {
    onChange(field, camelizeKeys(place));
    setSelectedPlaceInput(place);
  };

  /** Sets the selectable options and the unavailable rows together, so stale disabled rows can't outlive a list. */
  const applyOptions = (selectable, unavailable = NO_UNAVAILABLE_OPTIONS) => {
    setOptions(selectable);
    setUnavailableOptions(unavailable);
  };

  const logRequestError = error => console.error('[AutocompleteSelect] Error fetching places', error);

  const finishLoading = error => {
    if (error) logRequestError(error);
    setHasRequestError(Boolean(error));
    setOptionsLoaded(true);
  };

  // The list request failed, so it is still not loaded: the next click on the field retries it.
  const clearPlacesAndFinishLoading = error => {
    finishLoading(error);
    setOriginsLoaded(false);
    applyOptions([]);
    setPlaces([]);
  };

  // Applied last so it overrides the origin_hits/destination_hits the branches may set.
  const placesUrl = () => applyPlacesSortParam(buildPlacesUrl(), placesSort).href;

  /** Starts a list request and returns a check for whether it is still the latest one. */
  const startListRequest = () => {
    const requestId = ++listRequestIdRef.current;
    return () => requestId === listRequestIdRef.current;
  };

  const fetchPlaces = url =>
    fetch(url).then(response => {
      if (response.ok === false) {
        throw new Error(`Places request failed with status ${response.status}`);
      }

      return response.json();
    });

  const fetchTerminals = (showAfterFetch = true, filterValue) => {
    setOptionsLoaded(false);
    setHasRequestError(false);

    if (showAfterFetch) {
      setShowItems(true);
    }
    /**
     * After fetching terminals, it's not necessary to check if the initial slug
     * is being set up, to be able to open the destination list if the user change the origin
     */
    if (showingInitialSlug.current) showingInitialSlug.current = false;

    const isCurrentList = startListRequest();
    fetchPlaces(placesUrl()).then(
      data => {
        if (!isCurrentList()) return;
        const availablePlaces = filterByAllowedSlugs(data || [], allowedSlugs);
        const filtered = preparePlaces(availablePlaces);
        setOptionsLoaded(true);
        setOriginsLoaded(true);
        applyOptions(sortListOptions(filtered));
        setPlaces(availablePlaces);
        if (filterValue) filterOptions(filterValue);
        if (showAfterFetch) {
          optionsRef.current?.scrollTo(null, 0);
          if (getProductType() === 'web') currentInputRef()?.focus();
        }
      },
      error => {
        if (isCurrentList()) clearPlacesAndFinishLoading(error);
      }
    );
  };

  const fetchTerminalBy = slug => {
    settledInitialSlugRef.current = null;
    const lookupId = ++initialLookupIdRef.current;
    initialLookupStartedIdRef.current = lookupId;
    const finishInitialLookup = () => {
      if (lookupId === initialLookupIdRef.current) settledInitialSlugRef.current = slug;
    };
    const selectIfCurrent = place => {
      if (lookupId === initialLookupIdRef.current) {
        finishInitialLookup();
        selectInitialPlace(place);
      }
    };
    setOptionsLoaded(false);
    setHasRequestError(false);
    const isCurrentList = startListRequest();
    fetchPlaces(placesUrl()).then(
      data => {
        const availablePlaces = filterByAllowedSlugs(data || [], allowedSlugs);
        const filtered = preparePlaces(availablePlaces);
        // A newer list owns the options, but the initial place is still selected: only `lookupId` guards that.
        if (isCurrentList()) {
          setOriginsLoaded(true);
          setOptionsLoaded(true);
          setPlaces(availablePlaces);
          applyOptions(sortListOptions(filtered));
        }

        const initialPlaces = prepareInitialPlaces(availablePlaces);
        if (initialPlaces.length > 0) {
          const place = initialPlaces.find(p => {
            return p.slug === slug;
          });
          if (place) {
            selectIfCurrent(place);
            return null;
          }
        }

        return fetchPlaces(placeLookupUrl(slug)).then(
          lookupData => {
            const lookupFiltered = prepareInitialPlaces(lookupData);
            const place = lookupFiltered.find(p => p.slug === slug);
            finishInitialLookup();
            if (place) selectIfCurrent(place);
          },
          error => {
            finishInitialLookup();
            if (isCurrentList()) finishLoading(error);
          }
        );
      },
      error => {
        finishInitialLookup();
        if (isCurrentList()) clearPlacesAndFinishLoading(error);
      }
    );
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(typedRequestTimeoutRef.current);
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sets the initial place in the input and also it is selected for the search
   */
  const setInitialSlug = () => {
    if (!initialPlaceSlug) return;
    fetchTerminalBy(initialPlaceSlug);
  };

  useEffect(() => {
    setInitialSlug();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPlaceSlug]);

  // The base type can change after the first lookup (hybrid settles). A lookup that ran under the
  // old type may have dropped the initial place, so run it again unless the user has acted.
  useEffect(() => {
    const previousType = previousActiveBasePlaceTypeRef.current;
    previousActiveBasePlaceTypeRef.current = activeBasePlaceType;
    if (previousType === undefined) return;
    if (previousType === activeBasePlaceType) return;
    // search.js seeds `place` with a slug-only placeholder; only a place with an `id` is selected.
    if (!initialPlaceSlug || placeProp?.id) return;
    if (initialLookupStartedIdRef.current !== initialLookupIdRef.current) return;
    setInitialSlug();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBasePlaceType]);

  useEffect(() => {
    if (placeProp?.slug && placeProp?.id) {
      if (selectedPlaceRef.current !== placeProp || !isEditingInputRef.current) {
        setSelectedPlaceInput(decamelizeKeys(placeProp));
      }
      selectedPlaceRef.current = placeProp;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeProp, hybridSearchEnabled]);

  useEffect(() => {
    setDisabledInput(disabled);
  }, [disabled]);

  /**
   * The function `groupOptionsBy` groups an array of options by a specified property and returns an
   * array of objects with the grouped options.
   * @param {Object} options - An array of options to be grouped.
   * @param {string} groupBy - The property to group the options by.
   * @returns The function `groupOptionsBy` returns an array of objects. Each object in the array has
   * two properties: `group` and `options`. The `group` property represents the group name, and the
   * `options` property represents an array of options belonging to that group.
   */
  const groupOptionsBy = ({ options, groupBy }) => {
    if (!options.length) return [];
    const optionsReduced = options.reduce((acc, place) => {
      const key = place[groupBy] || PLACES_GROUP_FALLBACK;
      if (!acc[key]) {
        acc[key] = { group: key, options: [] };
      }
      acc[key].options.push(place);
      return acc;
    }, {});

    return Object.values(optionsReduced);
  };

  const handleElementSelect = () => {
    initialLookupIdRef.current += 1;
    settledInitialSlugRef.current = initialPlaceSlug;
    setShowItems(false);
    isMouseOverOptionsRef.current = false;
  };

  /**
   * Tracks if the mouse is over the options container.
   * This is used to prevent auto-selection when user is clicking an option.
   * @param {boolean} isOver - true when mouse enters, false when mouse leaves
   */
  const handleOptionsMouseState = (isOver) => {
    isMouseOverOptionsRef.current = isOver;
  };

  const getPlaceDisplayText = place => {
    if (isCityAllTerminalsPlace(place, hybridSearchEnabled)) {
      const state = displayType === 'city_state' ? `, ${place.state}` : '';
      return normalize(
        `${formatDisplayType(
          displayType,
          place,
          isPackagesSearch,
          undefined,
          hybridSearchEnabled
        )}${state}`
      );
    }

    const cityDisplay = isPackagesSearch ? '' : `, ${place.display}`;

    const displayTextMap = {
      city: `${place.city_name}${cityDisplay}`,
      city_state: `${place.city_name}, ${place.state} ${place.display}`,
      terminal_city: `${place.display}, ${place.city_name}`,
      state: `${place.display}, ${place.state}`,
      only_terminal: ` ${place.display}`,
    };

    return normalize(displayTextMap[displayType] || `${place.display}, ${place.state}`);
  };

  const queriedUrlFetch = query => {
    const { lat, long } = coordsToOrder || {};

    const url = createUrl(sourceUrl);

    if (query) {
      url.setQueryParam('q', query);
    }

    if (from) {
      url.setQueryParam('from', from);
    } else if (to) {
      url.setQueryParam('to', to);
    }

    if (lat && long && coordsToOrder) url.setQueryParam('lat', lat).setQueryParam('long', long);

    return applyPlacesSortParam(url, placesSearchSort).href;
  };

  /**
   * Orders a list for display and, when asked, moves the nearest terminal back to the top.
   *
   * Grouping by state becomes the primary key so that `groupOptionsBy`, which emits groups
   * in first-appearance order, produces alphabetical headings without a second sort. The
   * nearest terminal is an anchor rather than a list position, so it outranks whatever
   * ordering is in effect; with grouping on, that puts its own group first and the rest
   * alphabetically after it. Only the array this call just built is ever mutated.
   */
  const sortForDisplay = (list, criterion, pinNearest = true) => {
    const sorted = sortPlaces(list, {
      criterion,
      displayType,
      isPackagesSearch,
      groupBy: groupPlaces ? GROUP_FIELD : null,
    });

    if (!pinNearest || field !== 'origin' || !nearestTerminal?.id) return sorted;

    const nearestIndex = sorted.findIndex(place => place.id === nearestTerminal.id);
    if (nearestIndex > 0) sorted.unshift(...sorted.splice(nearestIndex, 1));

    return sorted;
  };

  const hasFilterValue = value =>
    Array.isArray(value) ? value.some(Boolean) : Boolean(value);

  const prioritizePlacesOnFilter = (value, places) => {
    if (!hasFilterValue(value)) return places;

    return prioritizeCityAllTerminalsOptions(places);
  };

  /**
   * Ordering for the list paths (prefetch and initial-slug resolution).
   *
   * The nearest terminal is re-pinned only for the alphabetical criterion, so the
   * popularity path stays exactly as it was. That is a rollout guard, not a claim that the
   * pin is redundant there: `nearestTerminal` is resolved by a different endpoint than the
   * list, so the two need not agree on which terminal is nearest.
   */
  const sortListOptions = list =>
    sortForDisplay(list, placesSort, placesSort === PLACES_SORT.ALPHABETICAL);

  const getPlacesToUse = list => sortForDisplay(list, placesSearchSort);

  /**
   * The allowed city all-terminals places in `list` that pass `matches`, only while the
   * opposite place uses one. That is the one case the rule hides a city that is otherwise
   * on offer, so it is the only one that gets a row: with the flag off nothing is hidden,
   * since cities stay ordinary places. Ordered by the search sort but never grouped or pinned, since these
   * rows are not selectable. The gate runs first so typing skips the corpus scan.
   */
  const toUnavailableOptions = (value, list, matches = () => true) => {
    const isGated = isCityAllTerminalsPlace(oppositePlace, hybridSearchEnabled);
    if (!isGated || !hasFilterValue(value)) return NO_UNAVAILABLE_OPTIONS;

    return sortPlaces(
      filterByAllowedSlugs(list, allowedSlugs).filter(
        place => isCityAllTerminalsPlace(place, hybridSearchEnabled) && matches(place)
      ),
      { criterion: placesSearchSort, displayType, isPackagesSearch, groupBy: null }
    );
  };

  /**
   * Every call supersedes the typed request before it, whether that request is still
   * debouncing or already in flight, so a late response can't overwrite newer options.
   * A superseded request never settles the loader, so the branches that apply options
   * without fetching settle it themselves. `unavailable` are the local matches the rule
   * hides; a fetched response replaces them with its own.
   */
  const setPlacesOrFetch = (value, places, unavailable) => {
    const requestId = ++typedRequestIdRef.current;
    const isCurrent = () => requestId === typedRequestIdRef.current;
    clearTimeout(typedRequestTimeoutRef.current);

    if (places.length > 0 || !allowFetch) {
      if (typedRequestLoadingRef.current) {
        typedRequestLoadingRef.current = false;
        setOptionsLoaded(true);
      }
      applyOptions(places.length > 0 ? prioritizePlacesOnFilter(value, getPlacesToUse(places)) : [], unavailable);
      return;
    }

    setUnavailableOptions(unavailable);

    typedRequestTimeoutRef.current = setTimeout(() => {
      typedRequestLoadingRef.current = true;
      setOptionsLoaded(false);
      setHasRequestError(false);
      fetchPlace(queriedUrlFetch(value))
        .then(response => {
          if (!isCurrent()) return;
          const filtered = preparePlaces(response);
          applyOptions(
            prioritizePlacesOnFilter(value, getPlacesToUse(filtered)),
            toUnavailableOptions(value, response || [])
          );
        })
        .catch(error => {
          if (!isCurrent()) return;
          logRequestError(error);
          setHasRequestError(true);
        })
        .finally(() => {
          if (!isCurrent()) return;
          typedRequestLoadingRef.current = false;
          setOptionsLoaded(true);
        });
    }, 500);
  };

  const filterOptions = value => {
    const normalizedValue = normalize(value).trim();
    // `city_state` matches every word on its own; `setPlacesOrFetch` keeps that array shape.
    const terms = displayType === 'city_state' ? normalizedValue.split(' ') : [normalizedValue];
    const filterValue = displayType === 'city_state' ? terms : normalizedValue;
    const matchesValue = place => {
      const text = getPlaceDisplayText(place);
      return terms.every(term => text.includes(term));
    };

    setPlacesOrFetch(
      filterValue,
      preparePlaces(places).filter(matchesValue),
      toUnavailableOptions(filterValue, places, matchesValue)
    );
  };

  useEffect(() => {
    // Rows from a fetched response outlive an empty corpus, so they clear here, not only below.
    if (!isCityAllTerminalsPlace(oppositePlace, hybridSearchEnabled)) {
      setUnavailableOptions(NO_UNAVAILABLE_OPTIONS);
    }
    const isTyping = hasFilterValue(input) && (!placeProp?.slug || isEditingInputRef.current);
    // A typed search re-runs even with no corpus: a pending request was built for the
    // previous opposite place, and only a new one supersedes it.
    if (!places.length && !isTyping) return;

    if (isTyping) {
      filterOptions(input);
    } else {
      applyOptions(sortListOptions(preparePlaces(places)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hybridSearchEnabled, defaultPlaceType, oppositePlace]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (optionsLoaded && !options.length && !hasRequestError) {
      debounceRef.current = setTimeout(() => {
        onNoSearchResults(field, input);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, optionsLoaded, hasRequestError]);

  /**
   * Clears the current field if it matches the other field.
   * Prevents selecting the same place for both origin and destination.
   */
  const preventSamePlace = (otherFieldSlug) => {
    if (placeProp && placeProp.slug === otherFieldSlug) {
      setInput('');
      onChange(field, {});
    }
  };

  /**
   * Adapts destination field based on origin selection.
   * - Filters destination places based on selected origin (?from=slug)
   * - Prevents selecting same place for both origin and destination
   * - Enables/disables destination field based on flow (normal vs destination-first)
   * - Auto-opens destination dropdown after origin selection
   */
  const adaptDestinationToOrigin = () => {
    // Only execute for destination field
    if (field !== 'destination') {
      return;
    }

    preventSamePlace(from);

    if (!from && !isDestinationFirstEnabled) {
      // Normal flow: disable destination without origin
      handleDisableField(true);
      setShowItems(false);
      if (!disabledInput) {
        setInput('');
        onChange(field, {});
      }
    } else if (!from && isDestinationFirstEnabled) {
      // Destination-first flow: enable field but DON'T auto-open dropdown
      handleDisableField(false);
      setShowItems(false);
    } else {
      // Origin selected: enable destination field
      handleDisableField(false);

      // Determine if dropdown should open
      let shouldOpenList = false;

      if (isDestinationFirstEnabled && from && !placeProp?.slug) {
        // Destination-first: open when origin is selected and destination is empty
        shouldOpenList = true;
      } else if (!isDestinationFirstEnabled) {
        // Normal flow: apply original logic
        shouldOpenList = (!originHasInitialSlug || didInitialOriginChanged()) &&
                        !placeProp?.slug &&
                        (!showingInitialSlug.current || didInitialOriginChanged());
      }

      fetchTerminals(shouldOpenList && canAutoOpenList);
    }
  };

  useLayoutEffect(() => {
    adaptDestinationToOrigin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, isDestinationFirstEnabled]);

  /**
   * Adapts origin field based on destination selection.
   * - Filters origin places based on selected destination (?to=slug)
   * - Prevents selecting same place for both origin and destination
   * - Auto-opens origin dropdown in destination-first flow
   */
  const adaptOriginToDestination = () => {
    // Only execute for origin field
    if (field !== 'origin') {
      return;
    }

    preventSamePlace(to);

    // If origins not loaded yet (first time)
    if (!originsLoaded) {
      // Destination-first flow: fetch and open dropdown when destination selected first
      if (isDestinationFirstEnabled && to && !placeProp?.slug) {
        fetchTerminals(canAutoOpenList);
      }
      // Normal flow: wait for user to click
      return;
    }

    // Origins already loaded - determine if dropdown should open
    let shouldOpenDropdown = false;

    if (isDestinationFirstEnabled && to && !placeProp?.slug) {
      // Destination-first: open when destination is selected and origin is empty
      shouldOpenDropdown = true;
    } else if (to && !placeProp.slug && !showingInitialSlug.current) {
      // Normal flow: open to show filtered origins based on destination
      shouldOpenDropdown = true;
    }

    fetchTerminals(shouldOpenDropdown && canAutoOpenList);
  };

  useEffect(() => {
    adaptOriginToDestination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, isDestinationFirstEnabled]);

  // If the order by geolocation was not enabled before, it is fetched the places again order by geolocation
  useEffect(() => {
    if (coordsToOrder && !orderByGeoFetched.current) {
      const valueToFilter = currentInputRef()?.value && normalize(currentInputRef()?.value);
      fetchTerminals(false, valueToFilter);
      orderByGeoFetched.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsToOrder]);

  const handleInputChange = event => {
    showingInitialSlug.current = false;
    initialLookupIdRef.current += 1;
    settledInitialSlugRef.current = initialPlaceSlug;
    isEditingInputRef.current = true;
    const newInput = normalize(event.target.value);
    setInput(newInput);
    filterOptions(newInput.trim());

    // Show items when user starts typing in destination-first mode
    if (field === 'destination' && isDestinationFirstEnabled && !from) {
      setShowItems(true);
      // Fetch terminals if not loaded yet
      if (!originsLoaded) {
        fetchTerminals(false);
      }
    }

    if (newInput === '') {
      onChange(field, {});
    }
    if (onInputChange && typeof onInputChange === 'function') {
      onInputChange(field, newInput);
    }
  };

  const handleInputClick = () => {
    const transformFieldToEvent = {
      origin: 'Origin',
      destination: 'Destination',
    };
    mixpanelTracker.trackInterestInSearchEvent({
      section: transformFieldToEvent[field] || field,
    });
    if ((field === 'origin' || field === 'place') && !originsLoaded) {
      fetchTerminals();
    }

    // Allow destination to fetch when isDestinationFirstEnabled is enabled
    // NOTE: In destination-first flow, the dropdown opens automatically via useEffect
    // when 'from' changes, but if the user clicks manually it should also work
    if (field === 'destination' && isDestinationFirstEnabled && !originsLoaded) {
      fetchTerminals();
    }

    // Open dropdown when options are already loaded and dropdown is closed
    if (originsLoaded && !showItems) {
      setShowItems(true);
    }
  };

  const handleOnClean = () => {
    initialLookupIdRef.current += 1;
    settledInitialSlugRef.current = initialPlaceSlug;
    if (field === 'origin') {
      setShowItems(true);
      currentInputRef()?.focus();
    }
    // Supersede a scheduled or in-flight typed lookup, so it can't restore the old query's results.
    typedRequestIdRef.current += 1;
    clearTimeout(typedRequestTimeoutRef.current);
    if (typedRequestLoadingRef.current) {
      typedRequestLoadingRef.current = false;
      setOptionsLoaded(true);
    }
    setInput('');
    // Only a loaded corpus can restore the list; without one the options are left alone, as before.
    if (places.length) {
      applyOptions(sortListOptions(preparePlaces(places)));
    } else {
      setUnavailableOptions(NO_UNAVAILABLE_OPTIONS);
    }
    onChange(field, {});
  };

  const handleOnCloseModal = () => {
    setShowItems(false);
  };

  /**
   * Handles the blur event on the input.
   * Auto-selects the first available result if:
   * - There is text in the input
   * - There are options available
   * - No valid place is already selected
   * - The mouse is NOT over the options container (user is clicking an option)
   */
  const handleOnBlur = () => {
    // Don't auto-select if mouse is over the options container
    // This means the user is about to click on an option
    if (isMouseOverOptionsRef.current) {
      return;
    }

    // Only auto-select if:
    // 1. There is text in the input
    // 2. There are options available
    // 3. No valid place is already selected
    if (input && options.length > 0 && !placeProp?.slug) {
      const firstOption = options[0];
      onChange(field, camelizeKeys(firstOption));
      setSelectedPlaceInput(firstOption);
      setShowItems(false);
    }
  };

  /**
   *
   * @param {Object} params - object with the params for function
   * @param {String} params.field - Field that sends the place or places selected
   * @param {Object} params.origin - Origin selected
   * @param {Object} params.destination - Destination selected
   */
  const handleOnSelectRecommended = ({ field, origin, destination }) => {
    if (!origin && !destination) {
      throw new Error('You must provide an origin or destination');
    }
    let placeObject = {};
    /**
     * If origin and destination are in the params, an object with both fields
     * is created because a full route was selected.
     * If there is just one field, the place object is just selected.
     */
    if (origin && destination) {
      placeObject.origin = camelizeKeys(origin);
      placeObject.destination = camelizeKeys(destination);
    } else {
      placeObject = camelizeKeys({ origin, destination }[field]);
    }
    onChange(field, placeObject);
    handleElementSelect();
  };

  const optionsToShow = groupPlaces ? groupOptionsBy({ options, groupBy: GROUP_FIELD }) : options;

  /**
   * `places` keeps the corpus before the city all-terminals rule so the options can be
   * re-derived when the flag or the opposite place changes. The context feeds "Destinos
   * populares", which is clickable too, so it gets the same rule as the dropdown.
   */
  const contextPlaces = preparePlaces(places);

  return (
    <SelectContext.Provider
      value={{
        displayType,
        isPackagesSearch,
        onChange,
        field,
        handleElementSelect,
        hybridSearchEnabled,
        defaultPlaceType: activeBasePlaceType,
      }}
    >
      <PlacesProvider places={contextPlaces} type={field} selectedOrigin={from} selectedDestination={to}>
        <SearchInput
          modalTitle={<Text id={`title.select_your_${field}`} />}
          field={field}
          error={error}
          hasArrow={hasArrow}
          containerRef={containerRef}
          label={label}
          placeholder={placeholder}
          disabledInput={disabledInput}
          handleOnChange={handleInputChange}
          handleOnFocus={onFocus}
          handleInputClick={handleInputClick}
          handleOnClean={handleOnClean}
          handleOnCloseModal={handleOnCloseModal}
          handleOnBlur={handleOnBlur}
          value={input}
          inputRef={inputRef}
          showItems={showItems}
          options={optionsToShow}
          unavailableOptions={unavailableOptions}
          optionsLoaded={optionsLoaded}
          optionsRef={optionsRef}
          flatVariant={flatVariant}
          nearestTerminal={showNearestTerminal && nearestTerminal}
          handleOnSelectRecommended={handleOnSelectRecommended}
          validPlace={Boolean(placeProp?.slug)}
          hasSelectedDestination={isDestinationFirstEnabled && Boolean(to)}
          hasSelectedOrigin={Boolean(from)}
          placesRankedByPopularity={placesSort === PLACES_SORT.POPULARITY}
          handleOptionsMouseState={handleOptionsMouseState}
        />
      </PlacesProvider>
    </SelectContext.Provider>
  );
};

export default AutocompleteSelect;
